/*
 * Cookie consent banner controller.
 *
 * Persistence: Shopify Customer Privacy API only. No NC-owned cookie.
 *   setTrackingConsent({analytics, marketing, preferences, sale_of_data}, cb)
 *
 * Re-show logic:
 *   - First load with no stored policy version → show banner.
 *   - Stored version older than current cookie_policy_version → show banner.
 *   - GPC on → suppress banner; auto-deny analytics + marketing.
 *
 * Footer link entry point: window.NCCookieBanner.openPreferences().
 *
 * Vanilla JS only. No jQuery, no third-party CMP.
 */
(function () {
  'use strict';

  if (window.__ncCookieBannerLoaded) return;
  window.__ncCookieBannerLoaded = true;

  var CFG = window.__ncCookieBannerConfig || {
    policyVersion: '2026-05-09',
    storageKey: 'nc_cookie_policy_version',
    apiTimeoutMs: 3000
  };

  var BANNER_ID = 'nc-cookie-banner';
  var FOCUSABLE_SEL = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // ---------------------------------------------------------------------------
  // Storage helpers — only stores policy version; consent state lives in Shopify
  // ---------------------------------------------------------------------------

  function readStoredVersion() {
    try {
      return window.localStorage.getItem(CFG.storageKey);
    } catch (e) {
      return null;
    }
  }

  function writeStoredVersion() {
    try {
      window.localStorage.setItem(CFG.storageKey, CFG.policyVersion);
    } catch (e) {
      // ignore quota / privacy-mode errors
    }
  }

  // ---------------------------------------------------------------------------
  // Customer Privacy API helpers
  // ---------------------------------------------------------------------------

  function getCustomerPrivacy() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  function setConsent(prefs, cb) {
    var cp = getCustomerPrivacy();
    if (!cp || typeof cp.setTrackingConsent !== 'function') {
      if (cb) cb();
      return;
    }
    try {
      cp.setTrackingConsent(prefs, function () {
        writeStoredVersion();
        if (cb) cb();
      });
    } catch (e) {
      if (cb) cb();
    }
  }

  function getCurrentConsent() {
    var cp = getCustomerPrivacy();
    if (!cp) return null;
    var prefs = {};
    try {
      if (typeof cp.analyticsProcessingAllowed === 'function') {
        prefs.analytics = cp.analyticsProcessingAllowed();
      }
      if (typeof cp.marketingAllowed === 'function') {
        prefs.marketing = cp.marketingAllowed();
      }
      if (typeof cp.preferencesProcessingAllowed === 'function') {
        prefs.preferences = cp.preferencesProcessingAllowed();
      }
      if (typeof cp.saleOfDataAllowed === 'function') {
        prefs.sale_of_data = cp.saleOfDataAllowed();
      }
    } catch (e) {
      // fall through
    }
    return prefs;
  }

  // ---------------------------------------------------------------------------
  // Banner DOM controller
  // ---------------------------------------------------------------------------

  var bannerEl = null;
  var primaryEl = null;
  var customizeEl = null;
  var lastFocused = null;
  var keydownBound = false;

  function getBanner() {
    if (!bannerEl) {
      bannerEl = document.getElementById(BANNER_ID);
      if (bannerEl) {
        primaryEl = bannerEl.querySelector('.nc-cookie-banner__primary');
        customizeEl = bannerEl.querySelector('.nc-cookie-banner__customize');
      }
    }
    return bannerEl;
  }

  function showBanner(viewMode) {
    var el = getBanner();
    if (!el) return;
    lastFocused = document.activeElement;
    el.hidden = false;
    setView(viewMode || 'primary');
    bindKeydown();
    // Defer focus so the dialog is rendered before focus moves
    window.setTimeout(function () {
      var first = el.querySelector(FOCUSABLE_SEL);
      if (first) first.focus();
    }, 0);
  }

  function hideBanner() {
    var el = getBanner();
    if (!el) return;
    el.hidden = true;
    unbindKeydown();
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try { lastFocused.focus(); } catch (e) { /* ignore */ }
    }
    lastFocused = null;
  }

  function setView(view) {
    if (!primaryEl || !customizeEl) return;
    if (view === 'customize') {
      primaryEl.hidden = true;
      primaryEl.setAttribute('aria-hidden', 'true');
      customizeEl.hidden = false;
      customizeEl.setAttribute('aria-hidden', 'false');
      // Pre-fill toggles from current consent state
      var current = getCurrentConsent() || {};
      var analyticsCb = customizeEl.querySelector('[data-nc-cookie-category="analytics"]');
      var marketingCb = customizeEl.querySelector('[data-nc-cookie-category="marketing"]');
      if (analyticsCb) analyticsCb.checked = current.analytics === true;
      if (marketingCb) marketingCb.checked = current.marketing === true;
    } else {
      primaryEl.hidden = false;
      primaryEl.setAttribute('aria-hidden', 'false');
      customizeEl.hidden = true;
      customizeEl.setAttribute('aria-hidden', 'true');
    }
  }

  function trapFocus(e) {
    var el = getBanner();
    if (!el) return;
    var focusable = Array.prototype.slice.call(el.querySelectorAll(FOCUSABLE_SEL))
      .filter(function (n) { return !n.hidden && n.offsetParent !== null; });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleReject();
    } else if (e.key === 'Tab') {
      trapFocus(e);
    }
  }

  function bindKeydown() {
    if (keydownBound) return;
    document.addEventListener('keydown', onKeydown);
    keydownBound = true;
  }

  function unbindKeydown() {
    if (!keydownBound) return;
    document.removeEventListener('keydown', onKeydown);
    keydownBound = false;
  }

  // ---------------------------------------------------------------------------
  // Action handlers
  // ---------------------------------------------------------------------------

  function handleAcceptAll() {
    setConsent({
      analytics: true,
      marketing: true,
      preferences: true,
      sale_of_data: true
    }, hideBanner);
  }

  function handleReject() {
    setConsent({
      analytics: false,
      marketing: false,
      preferences: false,
      sale_of_data: false
    }, hideBanner);
  }

  function handleCustomize() {
    setView('customize');
    var firstInput = customizeEl.querySelector('input:not([disabled])');
    if (firstInput) firstInput.focus();
  }

  function handleBack() {
    setView('primary');
    var firstBtn = primaryEl.querySelector('button');
    if (firstBtn) firstBtn.focus();
  }

  function handleSavePreferences() {
    var analyticsCb = customizeEl.querySelector('[data-nc-cookie-category="analytics"]');
    var marketingCb = customizeEl.querySelector('[data-nc-cookie-category="marketing"]');
    setConsent({
      analytics: analyticsCb ? !!analyticsCb.checked : false,
      marketing: marketingCb ? !!marketingCb.checked : false,
      preferences: false,
      sale_of_data: marketingCb ? !!marketingCb.checked : false
    }, hideBanner);
  }

  function bindActions() {
    var el = getBanner();
    if (!el) return;
    el.addEventListener('click', function (e) {
      var target = e.target.closest('[data-nc-cookie-action]');
      if (!target) return;
      var action = target.getAttribute('data-nc-cookie-action');
      switch (action) {
        case 'accept': handleAcceptAll(); break;
        case 'reject': handleReject(); break;
        case 'customize': handleCustomize(); break;
        case 'back': handleBack(); break;
        case 'save': handleSavePreferences(); break;
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Bootstrap — wait for Customer Privacy API, then decide whether to show
  // ---------------------------------------------------------------------------

  function isGPCEnabled() {
    return navigator.globalPrivacyControl === true;
  }

  function shouldShowBanner() {
    if (isGPCEnabled()) return false;
    var stored = readStoredVersion();
    if (!stored) return true;
    return stored !== CFG.policyVersion;
  }

  function applyGPCDefaults() {
    setConsent({
      analytics: false,
      marketing: false,
      preferences: false,
      sale_of_data: false
    });
  }

  function waitForCustomerPrivacy(cb) {
    if (getCustomerPrivacy()) { cb(true); return; }
    var start = Date.now();
    var iv = window.setInterval(function () {
      if (getCustomerPrivacy()) {
        window.clearInterval(iv);
        cb(true);
      } else if (Date.now() - start > CFG.apiTimeoutMs) {
        window.clearInterval(iv);
        cb(false);
      }
    }, 100);
  }

  function init() {
    if (!getBanner()) return;
    bindActions();

    waitForCustomerPrivacy(function (loaded) {
      if (!loaded) {
        // Fallback: API not available — banner stays hidden, footer link
        // remains as the only entry point via openPreferences().
        if (window.console && window.console.warn) {
          window.console.warn('Customer Privacy API not loaded');
        }
        return;
      }

      if (isGPCEnabled()) {
        applyGPCDefaults();
        return;
      }

      if (shouldShowBanner()) {
        showBanner('primary');
      }
    });
  }

  // Public surface for footer link (Chunk B) and any future programmatic re-prompt.
  window.NCCookieBanner = {
    openPreferences: function () {
      // If Customer Privacy API isn't loaded yet, still attempt to show the
      // banner — the user clicked the footer link, so they are explicitly
      // requesting it. setConsent() will no-op gracefully if API is absent.
      showBanner('customize');
    },
    open: function () {
      showBanner('primary');
    },
    close: hideBanner
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
